import React, {
  useCallback,
  useContext,
  useState,
} from 'react';
import { MenuItem, Button } from '@mui/material';
import PropTypes from 'prop-types';
import format from 'string-template';

import InputLabel from '../../../../../../../../components/v2/InputLabel';
import Select from '../../../../../../../../components/v2/Select';
import TextField from '../../../../../../../../components/v2/TextField';
import AnnouncementContext from '../../../../../../../context/AnnouncementContext';
import useToast from '../../../../../../../hooks/useToast';
import useCommunityCreation, { CommunityCreationStatus } from '../../../../../../../hooks/useCommunityCreation';
import { CommunityType } from '../../../../../../../Model/Community';
import {
  CommunitySection,
  CommunityHint,
  CommunityCreateBox,
  CommunityCreateActions,
} from './styles';
import texts from './texts.json';

// Sentinel Select value that opens the inline "create community" form.
const CREATE_COMMUNITY_OPTION = '__CREATE_COMMUNITY__';

const CommunitySelect = ({ coachId, value, onChange }) => {
  const { communities: coachCommunities } = useContext(AnnouncementContext);
  const { showToast } = useToast();
  const { createCommunity } = useCommunityCreation(coachId);

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [nameError, setNameError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const communities = (coachCommunities || []).filter((community) => community.type === CommunityType.CUSTOM);

  const handleSelectChange = useCallback((event) => {
    const { value: selected } = event.target;
    if (selected === CREATE_COMMUNITY_OPTION) {
      setShowCreate(true);
      return;
    }
    onChange(selected);
    setShowCreate(false);
  }, [onChange]);

  const handleCreate = useCallback(async () => {
    setIsProcessing(true);
    const result = await createCommunity({ name: newName });
    setIsProcessing(false);

    if (result.status === CommunityCreationStatus.NAME_TAKEN) {
      setNameError(texts.nameTaken);
      return;
    }
    if (result.status === CommunityCreationStatus.ERROR) {
      showToast(format(texts.createFailed, { error: result.message }), { error: true });
      return;
    }
    if (result.status === CommunityCreationStatus.CREATED) {
      onChange(result.id);
      setShowCreate(false);
      setNewName('');
      setNameError('');
    }
  }, [newName, createCommunity, onChange, showToast]);

  const cancelCreate = useCallback(() => {
    setShowCreate(false);
    setNewName('');
    setNameError('');
  }, []);

  return (
    <CommunitySection>
      <InputLabel>{texts.label}</InputLabel>
      <CommunityHint>{texts.hint}</CommunityHint>
      <Select
        displayEmpty
        value={showCreate ? CREATE_COMMUNITY_OPTION : value}
        onChange={handleSelectChange}
      >
        <MenuItem value="">{texts.none}</MenuItem>
        {communities.map((community) => (
          <MenuItem key={community.id} value={community.id}>{community.name}</MenuItem>
        ))}
        <MenuItem value={CREATE_COMMUNITY_OPTION}>{texts.createNew}</MenuItem>
      </Select>
      {showCreate && (
        <CommunityCreateBox>
          <InputLabel>{texts.newNameLabel}</InputLabel>
          <TextField
            value={newName}
            placeholder={texts.newNamePlaceholder}
            onChange={(e) => {
              setNewName(e.target.value);
              setNameError('');
            }}
            error={!!nameError}
            helperText={nameError}
            $withoutMargin
          />
          <CommunityCreateActions>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={!newName.trim() || isProcessing}
            >
              {texts.createAndSelect}
            </Button>
            <Button onClick={cancelCreate} disabled={isProcessing}>
              {texts.cancel}
            </Button>
          </CommunityCreateActions>
        </CommunityCreateBox>
      )}
    </CommunitySection>
  );
};

CommunitySelect.propTypes = {
  coachId: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

CommunitySelect.defaultProps = {
  coachId: '',
};

export default CommunitySelect;
